import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import customTableStyles from "../../../../global/components/CustomTable/customTable.styles";
import { Autocomplete, Box, TextField, Typography, Stack } from "@mui/material";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import { applyLicenseKey } from "../../../../utils/License";
import ShowAllStyles from "../../../AggregatorApi/components/ShowAll/ShowAll.styles";
import { getAllUnmapForm, updateUnmapForm } from "./ShowAllService";
import { getAllForms } from "../../../AllLeads/components/ShowAll/ShowAllService";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ShowAll = (_props: any) => {
  useEffect(() => {
    applyLicenseKey();
  }, []);

  const classes = ShowAllStyles;
  const customTableClasses = customTableStyles;
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [apiResponse, setApiResponse] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [unmapFormData, setUnmapFormData] = useState<any>([]);
  const [formData, setFormData] = useState<any>([]);
  const [metaData, setMetaData] = useState<any>({});
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    tableDataShowHandler();
  }, [paginationModel, search, activeOrganizationId]);

  const handleAutocompleteChange = async (data: any, _event: any, row: any) => {
    try {
      const isUnmap = !data || !data.id;
      const payload = {
        _id: row._id,
        map_form_id: isUnmap ? "" : data.id,
      };
      const response = await updateUnmapForm(payload);
      if (response.success === 1) {
        const listPayload = {
          pageNumber: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: search || "",
        };
        const listResponse = await getAllUnmapForm(listPayload);
        setUnmapFormData(listResponse.data);
        openSuccessNotification(
          isUnmap
            ? response?.message || "Form unmapped"
            : response?.message || "Form mapped"
        );
      } else {
        openErrorNotification(response?.message || "Update failed");
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Form Name",
      field: "form_name",
      minWidth: 350,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Typography sx={classes.fullName}>{params.row?.form_name}</Typography>
        </Stack>
      ),
    },
    {
      headerName: "Form Id",
      field: "form_id",
      minWidth: 160,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          {params.row?.form_id}
        </Stack>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Autocomplete
            clearOnEscape
            onChange={(event, newValue) => {
              handleAutocompleteChange(newValue, event, params.row);
            }}
            value={
              formData?.find((item: any) => item.id === params.row.map_form_id) ||
              null
            }
            isOptionEqualToValue={(option, value) =>
              Boolean(option?.id && value?.id && option.id === value.id)
            }
            style={{ width: "220px" }}
            sx={customTableClasses.autocompleteStyle}
            id={`map-form-${params.row._id}`}
            options={formData}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                placeholder={
                  params.row.map_form_id ? "Mapped — clear to unmap" : "Select your form"
                }
              />
            )}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: "12px" }}>
                {option.label}
              </li>
            )}
          />
        </div>
      ),
    },
  ];

  const tableDataShowHandler = async () => {
    try {
      setApiResponse(true);
      const payload = {
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || "",
      };
      const response = await getAllUnmapForm(payload);
      setUnmapFormData(response.data);
      setMetaData(response?.metadata[0] ? response?.metadata[0] : metaData);
      const forms = await getAllForms();
      setFormData(
        (forms.data || [])
          ?.filter((item: any) => item.status === "1")
          ?.map((i: any) => {
            return { id: i._id, label: i.form_name, formId: i.form_id };
          })
      );
      setApiResponse(false);
    } catch (error: any) {
      setApiResponse(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  return (
    <>
      <Box sx={{ mb: 2, maxWidth: 420 }}>
        <YhInput
          placeholder="Search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus={isSearchFocused}
          startAdornment={<SearchSvg />}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
        />
      </Box>
      <YhTable
        columns={columns}
        rows={unmapFormData || []}
        getRowId={(row) => row._id}
        rowHeight={70}
        loading={apiResponse}
        emptyMessage="No forms found"
        className="min-h-[320px]"
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        totalRows={metaData?.total || 0}
        onPageChange={(p) =>
          setPaginationModel((prev) => ({ ...prev, page: p }))
        }
        onPageSizeChange={(s) => setPaginationModel({ pageSize: s, page: 0 })}
      />
      {apiResponse && <CustomLoader apiResponse={false} />}
    </>
  );
};

export default ShowAll;
